import dayjs from 'dayjs';
import { FormValues, OnboardingStep } from './types';
import { CompanyExternalUser } from '../../../../data/models/companyExternalUser';

const normalizeBoolean = (
	value: string | boolean | undefined
): boolean | undefined => {
	if (typeof value === 'boolean') {
		return value;
	}
	if (value === 'yes') {
		return true;
	}
	if (value === 'no') {
		return false;
	}
	return undefined;
};

const sanitizeString = (value?: string): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeAddress = (address?: FormValues['homeAddress']) => {
	if (!address) {
		return undefined;
	}
	const cleaned = {
		line1: sanitizeString(address.line1),
		line2: sanitizeString(address.line2),
		city: sanitizeString(address.city),
		state: sanitizeString(address.state),
		zip: sanitizeString(address.zip),
	};
	const hasValues = Object.values(cleaned).some(
		(value) => value !== undefined
	);
	return hasValues ? cleaned : undefined;
};

export const createExternalDefaultValues = (): FormValues => ({
	firstName: '',
	lastName: '',
	preferredName: '',
	preferredPronouns: '',
	dateOfBirth: '',
	profilePictureUrl: undefined,
	email: '',
	phone: '',
	phoneCarrier: '',
	isIphone: '',
	smsTextReady: '',
	hasWhatsapp: '',
	whatsappNumber: '',
	hasWechat: '',
	wechatNumber: '',
	emergencyContactFirstName: '',
	emergencyContactLastName: '',
	emergencyContactPhone: '',
	homeAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},
	shippingIsSameAsHome: false,
	shippingAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},
	currentJobStatus: '',
	wantFreelanceContact: '',
	interestedInW2Positions: '',
	interestedInInternship: '',
	unionStatus: '',
	unionName: '',
	productionRole: '',
	yearsOfExperiencePrimary: '',
	rateTenOrTwelveHours: '',
	isRateFixed: undefined,
	chargesOvertimeTypically: undefined,
	workReferenceFirstName: '',
	workReferenceLastName: '',
	workReferenceEmail: '',
	workReferencePhone: '',
	workReference2FirstName: '',
	workReference2LastName: '',
	workReference2Email: '',
	workReference2Phone: '',
	secondaryProductionRole: '',
	secondaryRate: '',
	secondaryRateType: '',
	secondaryYearsOfExperience: '',
	secondaryRateTenOrTwelveHours: '',
	secondaryIsRateFixed: undefined,
	secondaryChargesOvertimeTypically: undefined,
	secondaryWorkReferenceFirstName: '',
	secondaryWorkReferenceLastName: '',
	secondaryWorkReferenceEmail: '',
	secondaryWorkReferencePhone: '',
	secondaryWorkReference2FirstName: '',
	secondaryWorkReference2LastName: '',
	secondaryWorkReference2Email: '',
	secondaryWorkReference2Phone: '',
	websiteUrl: '',
	instagramHandle: '',
	resumeProjectHistoryUploads: [],
	hasKit: false,
	kitContents: '',
	kitFee: '',
	kitDocuments: [],
	willingToBePhotographedOnSet: false,
	dietaryRestrictions: '',
	shirtSize: '',
	shoeSize: '',
	certifications: [],
	localWorkLocations: [''],
	employmentTypePreference: '',
	loanOutCompanyName: '',
	loanOutCompanyContactName: '',
	loanOutCompanyPhone: '',
	loanOutCompanyEmail: '',
	preferredPaymentMethods: [],
	hasRepresentation: undefined,
	representativeFullName: '',
	representativeCompany: '',
	representativePhone: '',
	representativeEmail: '',
	billThroughAgentCompany: '',
	globalEntry: '',
	passportIssuingCountry: '',
	passportExpirationDate: '',
	noPassport: undefined,
	willingToTravelDomestically: '',
	willingToTravelInternationally: '',
	driversLicenseState: '',
	noDriversLicense: undefined,
	hasVehicle: undefined,
	willingToUseVehicleForProduction: undefined,
	vehicleType: '',
	vehicleLocation: '',
	preferredHotels: [],
	preferredCarRentals: [],
	preferredRideSharingApps: [],
	unitedMileage: '',
	hiltonNumber: '',
	marriottBonvoyNumber: '',
	avisNumber: '',
	budgetNumber: '',
	driversLicenseNumber: '',
	streetAddress1: '',
	streetAddress2: '',
	city: '',
	state: '',
	zip: '',
	rate: '',
	rateType: '',
	homeAirport: '',
	passportNumber: '',
	tsaPrecheck: '',
	amtrackNumber: '',
	deltaMileage: '',
	preferredSeating: '',
	starwoodMember: '',
	emeraldClub: '',
	preferredAirlines: [],
});

export const mapExternalUserToFormValues = (
	externalUser: CompanyExternalUser
): FormValues => {
	const defaultValues = createExternalDefaultValues();
	const userProfile: any = externalUser?.userProfile || {};

	return {
		...defaultValues,
		firstName: externalUser.firstName || '',
		lastName: externalUser.lastName || '',
		preferredName: externalUser.preferredName || '',
		preferredPronouns: externalUser.preferredPronouns || '',
		dateOfBirth: externalUser.dateOfBirth || '',
		profilePictureUrl: userProfile?.profilePictureUrl,
		email: externalUser.email || '',
		phone: externalUser.phone || '',
		role: externalUser.role || '',
		groups: externalUser.groups?.map((group) => group.id) || [],
		phoneCarrier: userProfile?.phoneCarrier || '',
		isIphone: userProfile?.isIphone
			? 'yes'
			: userProfile?.isIphone === false
			? 'no'
			: '',
		smsTextReady: userProfile?.smsTextReady
			? 'yes'
			: userProfile?.smsTextReady === false
			? 'no'
			: '',
		hasWhatsapp: userProfile?.hasWhatsapp
			? 'yes'
			: userProfile?.hasWhatsapp === false
			? 'no'
			: '',
		whatsappNumber: userProfile?.whatsappNumber || '',
		hasWechat: userProfile?.hasWechat
			? 'yes'
			: userProfile?.hasWechat === false
			? 'no'
			: '',
		wechatNumber: userProfile?.wechatNumber || '',
		emergencyContactFirstName: userProfile?.emergencyContactFirstName || '',
		emergencyContactLastName: userProfile?.emergencyContactLastName || '',
		emergencyContactPhone: userProfile?.emergencyContactPhone || '',
		homeAddress: {
			line1: userProfile?.homeAddress?.line1 || '',
			line2: userProfile?.homeAddress?.line2 || '',
			city: userProfile?.homeAddress?.city || '',
			state: userProfile?.homeAddress?.state || '',
			zip: userProfile?.homeAddress?.zip || '',
		},
		shippingIsSameAsHome:
			Boolean(userProfile?.shippingAddress?.sameAsHomeAddress) || false,
		shippingAddress: {
			line1: userProfile?.shippingAddress?.line1 || '',
			line2: userProfile?.shippingAddress?.line2 || '',
			city: userProfile?.shippingAddress?.city || '',
			state: userProfile?.shippingAddress?.state || '',
			zip: userProfile?.shippingAddress?.zip || '',
		},
		currentJobStatus: userProfile?.currentJobStatus || '',
		wantFreelanceContact: userProfile?.wantFreelanceContact || '',
		interestedInW2Positions: userProfile?.interestedInW2Positions || '',
		interestedInInternship: userProfile?.interestedInInternship || '',
		unionStatus: userProfile?.unionStatus || '',
		unionName: userProfile?.unionName || '',
		productionRole: userProfile?.productionRole || '',
		yearsOfExperiencePrimary: userProfile?.yearsOfExperiencePrimary || '',
		rateTenOrTwelveHours: userProfile?.rateTenOrTwelveHours || '',
		isRateFixed: Boolean(userProfile?.isRateFixed) || undefined,
		chargesOvertimeTypically:
			Boolean(userProfile?.chargesOvertimeTypically) || undefined,
		workReferenceFirstName: userProfile?.workReferenceFirstName || '',
		workReferenceLastName: userProfile?.workReferenceLastName || '',
		workReferenceEmail: userProfile?.workReferenceEmail || '',
		workReferencePhone: userProfile?.workReferencePhone || '',
		workReference2FirstName: userProfile?.workReference2FirstName || '',
		workReference2LastName: userProfile?.workReference2LastName || '',
		workReference2Email: userProfile?.workReference2Email || '',
		workReference2Phone: userProfile?.workReference2Phone || '',
		secondaryProductionRole: userProfile?.secondaryProductionRole || '',
		secondaryRate: userProfile?.secondaryRate || '',
		secondaryRateType: userProfile?.secondaryRateType || '',
		secondaryYearsOfExperience:
			userProfile?.secondaryYearsOfExperience || '',
		secondaryRateTenOrTwelveHours:
			userProfile?.secondaryRateTenOrTwelveHours || '',
		secondaryIsRateFixed:
			Boolean(userProfile?.secondaryIsRateFixed) || undefined,
		secondaryChargesOvertimeTypically:
			Boolean(userProfile?.secondaryChargesOvertimeTypically) ||
			undefined,
		secondaryWorkReferenceFirstName:
			userProfile?.secondaryWorkReferenceFirstName || '',
		secondaryWorkReferenceLastName:
			userProfile?.secondaryWorkReferenceLastName || '',
		secondaryWorkReferenceEmail:
			userProfile?.secondaryWorkReferenceEmail || '',
		secondaryWorkReferencePhone:
			userProfile?.secondaryWorkReferencePhone || '',
		secondaryWorkReference2FirstName:
			userProfile?.secondaryWorkReference2FirstName || '',
		secondaryWorkReference2LastName:
			userProfile?.secondaryWorkReference2LastName || '',
		secondaryWorkReference2Email:
			userProfile?.secondaryWorkReference2Email || '',
		secondaryWorkReference2Phone:
			userProfile?.secondaryWorkReference2Phone || '',
		websiteUrl: userProfile?.websiteUrl || '',
		instagramHandle: userProfile?.instagramHandle || '',
		resumeProjectHistoryUploads:
			userProfile?.resumeProjectHistoryUploads || [],
		hasKit: Boolean(userProfile?.hasKit) || false,
		kitContents: userProfile?.kitContents || '',
		kitFee: userProfile?.kitFee || '',
		kitDocuments: userProfile?.kitDocuments || [],
		willingToBePhotographedOnSet:
			Boolean(userProfile?.willingToBePhotographedOnSet) || false,
		dietaryRestrictions: userProfile?.dietaryRestrictions || '',
		shirtSize: userProfile?.shirtSize || '',
		shoeSize: userProfile?.shoeSize || '',
		certifications: userProfile?.certifications || [],
		localWorkLocations: userProfile?.localWorkLocations || [''],
		employmentTypePreference: userProfile?.employmentTypePreference || '',
		loanOutCompanyName: userProfile?.loanOutCompanyName || '',
		loanOutCompanyContactName: userProfile?.loanOutCompanyContactName || '',
		loanOutCompanyPhone: userProfile?.loanOutCompanyPhone || '',
		loanOutCompanyEmail: userProfile?.loanOutCompanyEmail || '',
		preferredPaymentMethods: userProfile?.preferredPaymentMethods || [],
		hasRepresentation: userProfile?.hasRepresentation ?? undefined,
		representativeFullName: userProfile?.representativeFullName || '',
		representativeCompany: userProfile?.representativeCompany || '',
		representativePhone: userProfile?.representativePhone || '',
		representativeEmail: userProfile?.representativeEmail || '',
		billThroughAgentCompany: userProfile?.billThroughAgentCompany
			? 'yes'
			: userProfile?.billThroughAgentCompany === false
			? 'no'
			: '',
		globalEntry: userProfile?.globalEntry || '',
		passportIssuingCountry: userProfile?.passportIssuingCountry || '',
		passportExpirationDate: userProfile?.passportExpirationDate || '',
		noPassport: Boolean(userProfile?.noPassport) || undefined,
		willingToTravelDomestically: userProfile?.willingToTravelDomestically
			? 'yes'
			: userProfile?.willingToTravelInternationally === false
			? 'no'
			: '',
		willingToTravelInternationally:
			userProfile?.willingToTravelInternationally
				? 'yes'
				: userProfile?.willingToTravelInternationally === false
				? 'no'
				: '',
		driversLicenseState: userProfile?.driversLicenseState || '',
		noDriversLicense: Boolean(userProfile?.noDriversLicense) || undefined,
		hasVehicle: Boolean(userProfile?.hasVehicle) || undefined,
		willingToUseVehicleForProduction:
			Boolean(userProfile?.willingToUseVehicleForProduction) || undefined,
		vehicleType: userProfile?.vehicleType || '',
		vehicleLocation: userProfile?.vehicleLocation || '',
		preferredHotels: userProfile?.preferredHotels || [],
		preferredCarRentals: userProfile?.preferredCarRentals || [],
		preferredRideSharingApps: userProfile?.preferredRideSharingApps || [],
		unitedMileage: userProfile?.unitedMileage || '',
		hiltonNumber: userProfile?.hiltonNumber || '',
		marriottBonvoyNumber: userProfile?.marriottBonvoyNumber || '',
		avisNumber: userProfile?.avisNumber || '',
		budgetNumber: userProfile?.budgetNumber || '',
		driversLicenseNumber: userProfile?.driversLicenseNumber || '',
		streetAddress1: userProfile?.homeAddress?.line1 || '',
		streetAddress2: userProfile?.homeAddress?.line2 || '',
		city: userProfile?.homeAddress?.city || '',
		state: userProfile?.homeAddress?.state || '',
		zip: userProfile?.homeAddress?.zip || '',
		rate: userProfile?.rate || '',
		rateType: userProfile?.rateType || '',
		homeAirport: userProfile?.homeAirport || '',
		passportNumber: userProfile?.passportNumber || '',
		tsaPrecheck: userProfile?.tsaPrecheck || '',
		amtrackNumber: userProfile?.amtrackNumber || '',
		deltaMileage: userProfile?.deltaMileage || '',
		preferredSeating: userProfile?.preferredSeating || '',
		starwoodMember: userProfile?.starwoodMember || '',
		emeraldClub: userProfile?.emeraldClub || '',
		preferredAirlines: userProfile?.preferredAirlines || [],
	};
};

const removeEmptyAddress = (updateData: any) => {
	['homeAddress', 'shippingAddress'].forEach((key) => {
		const value = updateData[key];
		if (value && typeof value === 'object') {
			const entries = Object.entries(value as Record<string, unknown>);
			const filtered = entries.reduce<Record<string, unknown>>(
				(acc, [k, v]) => {
					if (v !== undefined && v !== '') {
						acc[k] = v;
					}
					return acc;
				},
				{}
			);
			if (Object.keys(filtered).length === 0) {
				delete updateData[key];
			} else {
				updateData[key] = filtered;
			}
		}
	});
};

export const buildExternalSavePayload = (
	values: FormValues,
	isFieldDirty: (fieldPath: string) => boolean
): any => {
	const updateData: any = {};

	if (isFieldDirty('firstName'))
		updateData.firstName = sanitizeString(values.firstName);
	if (isFieldDirty('lastName'))
		updateData.lastName = sanitizeString(values.lastName);
	if (isFieldDirty('email')) updateData.email = sanitizeString(values.email);
	if (isFieldDirty('phone')) updateData.phone = sanitizeString(values.phone);
	if (isFieldDirty('role')) updateData.role = sanitizeString(values.role);
	if (isFieldDirty('groups')) updateData.groupIds = values.groups;
	if (isFieldDirty('dateOfBirth'))
		updateData.dateOfBirth = values.dateOfBirth
			? dayjs(values.dateOfBirth).format('YYYY-MM-DD')
			: undefined;
	if (isFieldDirty('driversLicenseNumber'))
		updateData.driversLicenseNumber = sanitizeString(
			values.driversLicenseNumber
		);
	if (isFieldDirty('rate')) updateData.rate = sanitizeString(values.rate);
	if (isFieldDirty('rateType'))
		updateData.rateType = sanitizeString(values.rateType);
	if (isFieldDirty('dietaryRestrictions'))
		updateData.dietaryRestrictions = sanitizeString(
			values.dietaryRestrictions
		);
	if (isFieldDirty('homeAirport'))
		updateData.homeAirport = sanitizeString(values.homeAirport);
	if (isFieldDirty('passportNumber'))
		updateData.passportNumber = sanitizeString(values.passportNumber);
	if (isFieldDirty('tsaPrecheck'))
		updateData.tsaPrecheck = sanitizeString(values.tsaPrecheck);
	if (isFieldDirty('amtrackNumber'))
		updateData.amtrackNumber = sanitizeString(values.amtrackNumber);
	if (isFieldDirty('deltaMileage'))
		updateData.deltaMileage = sanitizeString(values.deltaMileage);
	if (isFieldDirty('preferredSeating'))
		updateData.preferredSeating = sanitizeString(values.preferredSeating);
	if (isFieldDirty('starwoodMember'))
		updateData.starwoodMember = sanitizeString(values.starwoodMember);
	if (isFieldDirty('emeraldClub'))
		updateData.emeraldClub = sanitizeString(values.emeraldClub);
	if (isFieldDirty('preferredAirlines'))
		updateData.preferredAirlines = values.preferredAirlines || [];

	if (isFieldDirty('preferredName'))
		updateData.preferredName = sanitizeString(values.preferredName);
	if (isFieldDirty('preferredPronouns'))
		updateData.preferredPronouns = sanitizeString(values.preferredPronouns);
	if (isFieldDirty('phoneCarrier'))
		updateData.phoneCarrier = sanitizeString(values.phoneCarrier);
	if (isFieldDirty('isIphone'))
		updateData.isIphone = normalizeBoolean(values.isIphone) ?? undefined;
	if (isFieldDirty('smsTextReady'))
		updateData.smsTextReady =
			normalizeBoolean(values.smsTextReady) ?? undefined;
	if (isFieldDirty('hasWhatsapp'))
		updateData.hasWhatsapp =
			normalizeBoolean(values.hasWhatsapp) ?? undefined;
	if (isFieldDirty('whatsappNumber'))
		updateData.whatsappNumber = sanitizeString(values.whatsappNumber);
	if (isFieldDirty('hasWechat'))
		updateData.hasWechat = normalizeBoolean(values.hasWechat) ?? undefined;
	if (isFieldDirty('wechatNumber'))
		updateData.wechatNumber = sanitizeString(values.wechatNumber);
	if (isFieldDirty('emergencyContactFirstName'))
		updateData.emergencyContactFirstName = sanitizeString(
			values.emergencyContactFirstName
		);
	if (isFieldDirty('emergencyContactLastName'))
		updateData.emergencyContactLastName = sanitizeString(
			values.emergencyContactLastName
		);
	if (isFieldDirty('emergencyContactPhone'))
		updateData.emergencyContactPhone = sanitizeString(
			values.emergencyContactPhone
		);
	if (isFieldDirty('currentJobStatus'))
		updateData.currentJobStatus = sanitizeString(values.currentJobStatus);
	if (isFieldDirty('wantFreelanceContact'))
		updateData.wantFreelanceContact = sanitizeString(
			values.wantFreelanceContact
		);
	if (isFieldDirty('interestedInW2Positions'))
		updateData.interestedInW2Positions = sanitizeString(
			values.interestedInW2Positions
		);
	if (isFieldDirty('interestedInInternship'))
		updateData.interestedInInternship = sanitizeString(
			values.interestedInInternship
		);
	if (isFieldDirty('unionStatus'))
		updateData.unionStatus = sanitizeString(values.unionStatus);
	if (isFieldDirty('unionName'))
		updateData.unionName = sanitizeString(values.unionName);
	if (isFieldDirty('productionRole'))
		updateData.productionRole = sanitizeString(values.productionRole);
	if (isFieldDirty('yearsOfExperiencePrimary'))
		updateData.yearsOfExperiencePrimary = sanitizeString(
			values.yearsOfExperiencePrimary
		);
	if (isFieldDirty('rateTenOrTwelveHours'))
		updateData.rateTenOrTwelveHours = sanitizeString(
			values.rateTenOrTwelveHours
		);
	if (isFieldDirty('isRateFixed'))
		updateData.isRateFixed = values.isRateFixed;
	if (isFieldDirty('chargesOvertimeTypically'))
		updateData.chargesOvertimeTypically = values.chargesOvertimeTypically;
	if (isFieldDirty('workReferenceFirstName'))
		updateData.workReferenceFirstName = sanitizeString(
			values.workReferenceFirstName
		);
	if (isFieldDirty('workReferenceLastName'))
		updateData.workReferenceLastName = sanitizeString(
			values.workReferenceLastName
		);
	if (isFieldDirty('workReferenceEmail'))
		updateData.workReferenceEmail = sanitizeString(
			values.workReferenceEmail
		);
	if (isFieldDirty('workReferencePhone'))
		updateData.workReferencePhone = sanitizeString(
			values.workReferencePhone
		);
	if (isFieldDirty('workReference2FirstName'))
		updateData.workReference2FirstName = sanitizeString(
			values.workReference2FirstName
		);
	if (isFieldDirty('workReference2LastName'))
		updateData.workReference2LastName = sanitizeString(
			values.workReference2LastName
		);
	if (isFieldDirty('workReference2Email'))
		updateData.workReference2Email = sanitizeString(
			values.workReference2Email
		);
	if (isFieldDirty('workReference2Phone'))
		updateData.workReference2Phone = sanitizeString(
			values.workReference2Phone
		);
	if (isFieldDirty('secondaryProductionRole'))
		updateData.secondaryProductionRole = sanitizeString(
			values.secondaryProductionRole
		);
	if (isFieldDirty('secondaryRate'))
		updateData.secondaryRate = sanitizeString(values.secondaryRate);
	if (isFieldDirty('secondaryRateType'))
		updateData.secondaryRateType = sanitizeString(values.secondaryRateType);
	if (isFieldDirty('secondaryYearsOfExperience'))
		updateData.secondaryYearsOfExperience = sanitizeString(
			values.secondaryYearsOfExperience
		);
	if (isFieldDirty('secondaryRateTenOrTwelveHours'))
		updateData.secondaryRateTenOrTwelveHours = sanitizeString(
			values.secondaryRateTenOrTwelveHours
		);
	if (isFieldDirty('secondaryIsRateFixed'))
		updateData.secondaryIsRateFixed = values.secondaryIsRateFixed;
	if (isFieldDirty('secondaryChargesOvertimeTypically'))
		updateData.secondaryChargesOvertimeTypically =
			values.secondaryChargesOvertimeTypically;
	if (isFieldDirty('secondaryWorkReferenceFirstName'))
		updateData.secondaryWorkReferenceFirstName = sanitizeString(
			values.secondaryWorkReferenceFirstName
		);
	if (isFieldDirty('secondaryWorkReferenceLastName'))
		updateData.secondaryWorkReferenceLastName = sanitizeString(
			values.secondaryWorkReferenceLastName
		);
	if (isFieldDirty('secondaryWorkReferenceEmail'))
		updateData.secondaryWorkReferenceEmail = sanitizeString(
			values.secondaryWorkReferenceEmail
		);
	if (isFieldDirty('secondaryWorkReferencePhone'))
		updateData.secondaryWorkReferencePhone = sanitizeString(
			values.secondaryWorkReferencePhone
		);
	if (isFieldDirty('secondaryWorkReference2FirstName'))
		updateData.secondaryWorkReference2FirstName = sanitizeString(
			values.secondaryWorkReference2FirstName
		);
	if (isFieldDirty('secondaryWorkReference2LastName'))
		updateData.secondaryWorkReference2LastName = sanitizeString(
			values.secondaryWorkReference2LastName
		);
	if (isFieldDirty('secondaryWorkReference2Email'))
		updateData.secondaryWorkReference2Email = sanitizeString(
			values.secondaryWorkReference2Email
		);
	if (isFieldDirty('secondaryWorkReference2Phone'))
		updateData.secondaryWorkReference2Phone = sanitizeString(
			values.secondaryWorkReference2Phone
		);
	if (isFieldDirty('websiteUrl'))
		updateData.websiteUrl = sanitizeString(values.websiteUrl);
	if (isFieldDirty('instagramHandle'))
		updateData.instagramHandle = sanitizeString(values.instagramHandle);
	if (isFieldDirty('resumeProjectHistoryUploads'))
		updateData.resumeProjectHistoryUploads =
			values.resumeProjectHistoryUploads;
	if (isFieldDirty('hasKit')) updateData.hasKit = values.hasKit;
	if (isFieldDirty('kitContents'))
		updateData.kitContents = sanitizeString(values.kitContents);
	if (isFieldDirty('kitFee'))
		updateData.kitFee = sanitizeString(values.kitFee);
	if (isFieldDirty('kitDocuments'))
		updateData.kitDocuments = values.kitDocuments;
	if (isFieldDirty('willingToBePhotographedOnSet'))
		updateData.willingToBePhotographedOnSet =
			values.willingToBePhotographedOnSet;
	if (isFieldDirty('shirtSize'))
		updateData.shirtSize = sanitizeString(values.shirtSize);
	if (isFieldDirty('shoeSize'))
		updateData.shoeSize = sanitizeString(values.shoeSize);
	if (isFieldDirty('certifications'))
		updateData.certifications = values.certifications;
	if (isFieldDirty('localWorkLocations'))
		updateData.localWorkLocations = values.localWorkLocations;
	if (isFieldDirty('employmentTypePreference'))
		updateData.employmentTypePreference = sanitizeString(
			values.employmentTypePreference
		);
	if (isFieldDirty('loanOutCompanyName'))
		updateData.loanOutCompanyName = sanitizeString(
			values.loanOutCompanyName
		);
	if (isFieldDirty('loanOutCompanyContactName'))
		updateData.loanOutCompanyContactName = sanitizeString(
			values.loanOutCompanyContactName
		);
	if (isFieldDirty('loanOutCompanyPhone'))
		updateData.loanOutCompanyPhone = sanitizeString(
			values.loanOutCompanyPhone
		);
	if (isFieldDirty('loanOutCompanyEmail'))
		updateData.loanOutCompanyEmail = sanitizeString(
			values.loanOutCompanyEmail
		);
	if (isFieldDirty('preferredPaymentMethods'))
		updateData.preferredPaymentMethods = values.preferredPaymentMethods;
	if (isFieldDirty('hasRepresentation'))
		updateData.hasRepresentation = values.hasRepresentation;
	if (isFieldDirty('representativeFullName'))
		updateData.representativeFullName = sanitizeString(
			values.representativeFullName
		);
	if (isFieldDirty('representativeCompany'))
		updateData.representativeCompany = sanitizeString(
			values.representativeCompany
		);
	if (isFieldDirty('representativePhone'))
		updateData.representativePhone = sanitizeString(
			values.representativePhone
		);
	if (isFieldDirty('representativeEmail'))
		updateData.representativeEmail = sanitizeString(
			values.representativeEmail
		);
	if (isFieldDirty('billThroughAgentCompany'))
		updateData.billThroughAgentCompany =
			normalizeBoolean(values.billThroughAgentCompany) ?? undefined;
	if (isFieldDirty('globalEntry'))
		updateData.globalEntry = sanitizeString(values.globalEntry);
	if (isFieldDirty('passportIssuingCountry'))
		updateData.passportIssuingCountry = sanitizeString(
			values.passportIssuingCountry
		);
	if (isFieldDirty('passportExpirationDate'))
		updateData.passportExpirationDate = values.passportExpirationDate
			? dayjs(values.passportExpirationDate).format('YYYY-MM-DD')
			: undefined;
	if (isFieldDirty('noPassport')) updateData.noPassport = values.noPassport;
	if (isFieldDirty('willingToTravelDomestically'))
		updateData.willingToTravelDomestically =
			normalizeBoolean(values.willingToTravelDomestically) ?? undefined;
	if (isFieldDirty('willingToTravelInternationally'))
		updateData.willingToTravelInternationally =
			normalizeBoolean(values.willingToTravelInternationally) ??
			undefined;
	if (isFieldDirty('driversLicenseState'))
		updateData.driversLicenseState = sanitizeString(
			values.driversLicenseState
		);
	if (isFieldDirty('noDriversLicense'))
		updateData.noDriversLicense = values.noDriversLicense;
	if (isFieldDirty('hasVehicle')) updateData.hasVehicle = values.hasVehicle;
	if (isFieldDirty('willingToUseVehicleForProduction'))
		updateData.willingToUseVehicleForProduction =
			values.willingToUseVehicleForProduction;
	if (isFieldDirty('vehicleType'))
		updateData.vehicleType = sanitizeString(values.vehicleType);
	if (isFieldDirty('vehicleLocation'))
		updateData.vehicleLocation = sanitizeString(values.vehicleLocation);
	if (isFieldDirty('preferredHotels'))
		updateData.preferredHotels = values.preferredHotels || [];
	if (isFieldDirty('preferredCarRentals'))
		updateData.preferredCarRentals = values.preferredCarRentals || [];
	if (isFieldDirty('preferredRideSharingApps'))
		updateData.preferredRideSharingApps =
			values.preferredRideSharingApps || [];
	if (isFieldDirty('unitedMileage'))
		updateData.unitedMileage = sanitizeString(values.unitedMileage);
	if (isFieldDirty('hiltonNumber'))
		updateData.hiltonNumber = sanitizeString(values.hiltonNumber);
	if (isFieldDirty('marriottBonvoyNumber'))
		updateData.marriottBonvoyNumber = sanitizeString(
			values.marriottBonvoyNumber
		);
	if (isFieldDirty('avisNumber'))
		updateData.avisNumber = sanitizeString(values.avisNumber);
	if (isFieldDirty('budgetNumber'))
		updateData.budgetNumber = sanitizeString(values.budgetNumber);

	if (
		isFieldDirty('homeAddress.line1') ||
		isFieldDirty('homeAddress.line2') ||
		isFieldDirty('homeAddress.city') ||
		isFieldDirty('homeAddress.state') ||
		isFieldDirty('homeAddress.zip')
	) {
		updateData.homeAddress = sanitizeAddress(values.homeAddress);
	}

	if (
		isFieldDirty('shippingAddress.line1') ||
		isFieldDirty('shippingAddress.line2') ||
		isFieldDirty('shippingAddress.city') ||
		isFieldDirty('shippingAddress.state') ||
		isFieldDirty('shippingAddress.zip') ||
		isFieldDirty('shippingIsSameAsHome')
	) {
		updateData.shippingAddress = {
			...sanitizeAddress(values.shippingAddress),
			sameAsHomeAddress: Boolean(values.shippingIsSameAsHome),
		};
	}

	removeEmptyAddress(updateData);

	return Object.keys(updateData).length > 0 ? updateData : null;
};

export const buildExternalSubmitPayload = (values: FormValues): any => ({
	firstName: sanitizeString(values.firstName),
	lastName: sanitizeString(values.lastName),
	preferredName: sanitizeString(values.preferredName),
	preferredPronouns: sanitizeString(values.preferredPronouns),
	dateOfBirth: values.dateOfBirth,
	profilePictureUrl: values.profilePictureUrl,
	email: sanitizeString(values.email),
	phone: sanitizeString(values.phone),
	phoneCarrier: sanitizeString(values.phoneCarrier),
	isIphone: normalizeBoolean(values.isIphone) ?? undefined,
	smsTextReady: normalizeBoolean(values.smsTextReady) ?? undefined,
	hasWhatsapp: normalizeBoolean(values.hasWhatsapp) ?? undefined,
	whatsappNumber: sanitizeString(values.whatsappNumber),
	hasWechat: normalizeBoolean(values.hasWechat) ?? undefined,
	wechatNumber: sanitizeString(values.wechatNumber),
	emergencyContactFirstName: sanitizeString(values.emergencyContactFirstName),
	emergencyContactLastName: sanitizeString(values.emergencyContactLastName),
	emergencyContactPhone: sanitizeString(values.emergencyContactPhone),
	homeAddress: sanitizeAddress(values.homeAddress),
	shippingAddress: {
		...sanitizeAddress(values.shippingAddress),
		sameAsHomeAddress: Boolean(values.shippingIsSameAsHome),
	},
	currentJobStatus: sanitizeString(values.currentJobStatus),
	wantFreelanceContact: sanitizeString(values.wantFreelanceContact),
	interestedInW2Positions: sanitizeString(values.interestedInW2Positions),
	interestedInInternship: sanitizeString(values.interestedInInternship),
	unionStatus: sanitizeString(values.unionStatus),
	unionName: sanitizeString(values.unionName),
	productionRole: sanitizeString(values.productionRole),
	yearsOfExperiencePrimary: sanitizeString(values.yearsOfExperiencePrimary),
	rateTenOrTwelveHours: sanitizeString(values.rateTenOrTwelveHours),
	isRateFixed: values.isRateFixed,
	chargesOvertimeTypically: values.chargesOvertimeTypically,
	workReferenceFirstName: sanitizeString(values.workReferenceFirstName),
	workReferenceLastName: sanitizeString(values.workReferenceLastName),
	workReferenceEmail: sanitizeString(values.workReferenceEmail),
	workReferencePhone: sanitizeString(values.workReferencePhone),
	workReference2FirstName: sanitizeString(values.workReference2FirstName),
	workReference2LastName: sanitizeString(values.workReference2LastName),
	workReference2Email: sanitizeString(values.workReference2Email),
	workReference2Phone: sanitizeString(values.workReference2Phone),
	secondaryProductionRole: sanitizeString(values.secondaryProductionRole),
	secondaryRate: sanitizeString(values.secondaryRate),
	secondaryRateType: sanitizeString(values.secondaryRateType),
	secondaryYearsOfExperience: sanitizeString(
		values.secondaryYearsOfExperience
	),
	secondaryRateTenOrTwelveHours: sanitizeString(
		values.secondaryRateTenOrTwelveHours
	),
	secondaryIsRateFixed: values.secondaryIsRateFixed,
	secondaryChargesOvertimeTypically: values.secondaryChargesOvertimeTypically,
	secondaryWorkReferenceFirstName: sanitizeString(
		values.secondaryWorkReferenceFirstName
	),
	secondaryWorkReferenceLastName: sanitizeString(
		values.secondaryWorkReferenceLastName
	),
	secondaryWorkReferenceEmail: sanitizeString(
		values.secondaryWorkReferenceEmail
	),
	secondaryWorkReferencePhone: sanitizeString(
		values.secondaryWorkReferencePhone
	),
	secondaryWorkReference2FirstName: sanitizeString(
		values.secondaryWorkReference2FirstName
	),
	secondaryWorkReference2LastName: sanitizeString(
		values.secondaryWorkReference2LastName
	),
	secondaryWorkReference2Email: sanitizeString(
		values.secondaryWorkReference2Email
	),
	secondaryWorkReference2Phone: sanitizeString(
		values.secondaryWorkReference2Phone
	),
	websiteUrl: sanitizeString(values.websiteUrl),
	instagramHandle: sanitizeString(values.instagramHandle),
	resumeProjectHistoryUploads: values.resumeProjectHistoryUploads,
	hasKit: values.hasKit,
	kitContents: sanitizeString(values.kitContents),
	kitFee: sanitizeString(values.kitFee),
	kitDocuments: values.kitDocuments,
	willingToBePhotographedOnSet: values.willingToBePhotographedOnSet,
	dietaryRestrictions: sanitizeString(values.dietaryRestrictions),
	shirtSize: sanitizeString(values.shirtSize),
	shoeSize: sanitizeString(values.shoeSize),
	certifications: values.certifications,
	localWorkLocations: values.localWorkLocations,
	employmentTypePreference: sanitizeString(values.employmentTypePreference),
	loanOutCompanyName: sanitizeString(values.loanOutCompanyName),
	loanOutCompanyContactName: sanitizeString(values.loanOutCompanyContactName),
	loanOutCompanyPhone: sanitizeString(values.loanOutCompanyPhone),
	loanOutCompanyEmail: sanitizeString(values.loanOutCompanyEmail),
	preferredPaymentMethods: values.preferredPaymentMethods,
	hasRepresentation: values.hasRepresentation,
	representativeFullName: sanitizeString(values.representativeFullName),
	representativeCompany: sanitizeString(values.representativeCompany),
	representativePhone: sanitizeString(values.representativePhone),
	representativeEmail: sanitizeString(values.representativeEmail),
	billThroughAgentCompany:
		normalizeBoolean(values.billThroughAgentCompany) ?? undefined,
	globalEntry: sanitizeString(values.globalEntry),
	passportIssuingCountry: sanitizeString(values.passportIssuingCountry),
	passportExpirationDate: values.passportExpirationDate || undefined,
	noPassport: values.noPassport,
	willingToTravelDomestically:
		normalizeBoolean(values.willingToTravelDomestically) ?? undefined,
	willingToTravelInternationally:
		normalizeBoolean(values.willingToTravelInternationally) ?? undefined,
	driversLicenseState: sanitizeString(values.driversLicenseState),
	noDriversLicense: values.noDriversLicense,
	hasVehicle: values.hasVehicle,
	willingToUseVehicleForProduction: values.willingToUseVehicleForProduction,
	vehicleType: sanitizeString(values.vehicleType),
	vehicleLocation: sanitizeString(values.vehicleLocation),
	preferredHotels: values.preferredHotels,
	preferredCarRentals: values.preferredCarRentals,
	preferredRideSharingApps: values.preferredRideSharingApps,
	unitedMileage: sanitizeString(values.unitedMileage),
	hiltonNumber: sanitizeString(values.hiltonNumber),
	marriottBonvoyNumber: sanitizeString(values.marriottBonvoyNumber),
	avisNumber: sanitizeString(values.avisNumber),
	budgetNumber: sanitizeString(values.budgetNumber),
	driversLicenseNumber: sanitizeString(values.driversLicenseNumber),
	rate: sanitizeString(values.rate),
	rateType: sanitizeString(values.rateType),
	homeAirport: sanitizeString(values.homeAirport),
	passportNumber: sanitizeString(values.passportNumber),
	tsaPrecheck: sanitizeString(values.tsaPrecheck),
	amtrackNumber: sanitizeString(values.amtrackNumber),
	deltaMileage: sanitizeString(values.deltaMileage),
	preferredSeating: sanitizeString(values.preferredSeating),
	starwoodMember: sanitizeString(values.starwoodMember),
	emeraldClub: sanitizeString(values.emeraldClub),
	preferredAirlines: values.preferredAirlines,
});

export const isFormValid = (
	values: FormValues,
	currentStep: OnboardingStep
): boolean => {
	switch (currentStep) {
		case OnboardingStep.BASIC_INFO:
			const basicFieldsValid = [
				'firstName',
				'lastName',
				'preferredName',
				'dateOfBirth',
				'email',
				'phone',
				'phoneCarrier',
				'emergencyContactFirstName',
				'emergencyContactLastName',
				'emergencyContactPhone',
			].every((field) => values[field]?.trim() !== '');

			const homeAddressValid =
				values.homeAddress?.line1?.trim() !== '' &&
				values.homeAddress?.city?.trim() !== '' &&
				values.homeAddress?.state?.trim() !== '' &&
				values.homeAddress?.zip?.trim() !== '';

			return basicFieldsValid && homeAddressValid;

		case OnboardingStep.PROFESSIONAL_DETAILS:
			const professionalFieldsValid = [
				'unionStatus',
				'productionRole',
				'rate',
				'yearsOfExperiencePrimary',
				'rateType',
				'rateTenOrTwelveHours',
				'workReferenceFirstName',
				'workReferenceLastName',
				'workReferenceEmail',
				'workReferencePhone',
			].every((field) => values[field]?.trim() !== '');

			// Check boolean fields
			const booleanFieldsValid = [
				'isRateFixed',
				'chargesOvertimeTypically',
			].every((field) => values[field] !== undefined);

			return professionalFieldsValid && booleanFieldsValid;

		case OnboardingStep.VEHICLE_TRAVEL:
			// Check if user has a driver's license (not "noDriversLicense")
			const hasDriversLicense = !values.noDriversLicense;
			const driversLicenseValid = hasDriversLicense
				? values.driversLicenseNumber?.trim() !== ''
				: true;

			const vehicleTravelFieldsValid = [
				'homeAirport',
				'willingToTravelDomestically',
				'willingToTravelInternationally',
			].every((field) => values[field]?.trim() !== '');

			return vehicleTravelFieldsValid && driversLicenseValid;

		case OnboardingStep.WORK_LOGISTICS:
			// Check if at least one local work location is filled
			const hasValidWorkLocation =
				values.localWorkLocations?.some((loc) => loc?.trim()) || false;
			return (
				hasValidWorkLocation &&
				!!values.employmentTypePreference?.trim()
			);

		case OnboardingStep.TRAVEL_PREFERENCES:
		case OnboardingStep.REPRESENTATION:
		case OnboardingStep.REVIEW:
			// These steps don't have required fields based on the component validation rules
			return true;

		default:
			return false;
	}
};
